import { isEqual, isNull, set } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { SaveFile } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import styles from './ClientRiskTolerance.module.scss';
import { getCurrentRiskToleranceConfig, getIsSaving, getRiskFieldsUpdated, getRiskFieldsUpdatedModal, saveRiskToleranceStarted, setRiskFieldsUpdated, toggleRiskFieldsUpdatedModal } from './store';
import { RiskToleranceUpdateBehaviour, UpdatedRiskField } from '../risk-tolerance/store';
import { RadioRow } from '../../shared/radio/RadioRow';
import { Scrollable } from '../../shared/scrollable/Scrollable';

const { lightestGrey, white } = styles;

const helpInfo = [
    {
        id: RiskToleranceUpdateBehaviour.MAINTAIN,
        label: 'Maintain',
        description: 'Selecting this option will NOT recalculate the field risk for any overwritten risk configurations for this field within your document portfolio'
    },
    {
        id: RiskToleranceUpdateBehaviour.RESET,
        label: 'Reset',
        description: 'Selecting this option will reset any overwritten risks for this field within your document portfolio back to the value as calculated, based on the risk configured'
    }
];

export const UpdatedRiskFieldsModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(getRiskFieldsUpdatedModal);
    const updatedRiskFields = useAppSelector(getRiskFieldsUpdated);
    const riskToleranceConfig = useAppSelector(getCurrentRiskToleranceConfig);
    const isSaving = useAppSelector(getIsSaving);

    const confirmDisabled = useMemo(() => updatedRiskFields.some(({ behaviour }) => isNull(behaviour)) || isSaving, [updatedRiskFields, isSaving]);

    const getFieldLabel = useCallback((riskField: UpdatedRiskField) => riskToleranceConfig?.find(({ datasetId }) => datasetId === riskField.datasetId)?.config.find(({ sectionId, fieldId }) => isEqual({ fieldId, sectionId }, { fieldId: riskField.fieldId, sectionId: riskField.sectionId }))?.label || '', [riskToleranceConfig]);

    const getRadioOptions = useCallback((riskFieldBehaviour: RiskToleranceUpdateBehaviour | null) => ([
        {
            id: RiskToleranceUpdateBehaviour.MAINTAIN,
            label: 'Maintain',
            isSelected: riskFieldBehaviour === RiskToleranceUpdateBehaviour.MAINTAIN,
        },
        {
            id: RiskToleranceUpdateBehaviour.RESET,
            label: 'Reset',
            isSelected: riskFieldBehaviour === RiskToleranceUpdateBehaviour.RESET,
        }
    ]), []);

    const updateRiskFieldBehaviour = useCallback((radioOptionId: RiskToleranceUpdateBehaviour, field: UpdatedRiskField) => {
        const riskFields = updatedRiskFields.map(riskField => isEqual(riskField, field) ? set('behaviour', radioOptionId, riskField) : riskField);
        dispatch(setRiskFieldsUpdated(riskFields));
    }, [dispatch, updatedRiskFields]);

    const saveRiskTolerance = useCallback(() => dispatch(saveRiskToleranceStarted()), [dispatch]);
    const closeModal = useCallback(() => dispatch(toggleRiskFieldsUpdatedModal(false)), [dispatch]);

    if (!isOpen) {
        return null;
    }

    return (
        <ConfirmationModal
            isOpen
            closeModal={closeModal}
            confirm={saveRiskTolerance}
            closeOnOverlayClick={false}
            confirmLabel='Save'
            confirmDisabled={confirmDisabled}
            showConfirmSpinner={isSaving}
        >
            <div className={styles.updatedRiskFieldsModal}>
                <ModalHeader icon={SaveFile} label='Override Risk Field Behaviour' />
                <div className={styles.updatedRiskFieldsInfo}>
                    <div className={styles.overrideDisclaimer}>Some of your documents include risks which have been overwritten.</div>
                    <div className={styles.overrideQuestion}>While we update the changes to the risk configuration, would you like the system to maintain the overwritten risk score or recalculate the risk based on updated configuration?</div>
                    {helpInfo.map(({ id, label, description }) => (
                        <div key={id} className={styles.helpInfoOption}>
                            <div className={styles.helpInfoLabel}>{label}</div>
                            <div className={styles.helpInfoDescription}>{description}</div>
                        </div>
                    ))}
                </div>
                <div className={styles.updatedRiskFieldsWrapper}>
                    <Scrollable>
                        {updatedRiskFields.map((riskField, index) => {
                            const { fieldId, behaviour } = riskField;
                            const label = getFieldLabel(riskField);
                            const radioOptions = getRadioOptions(behaviour);
                            return (
                                <div key={fieldId} className={styles.updatedRiskField} style={{ backgroundColor: index % 2 ? lightestGrey : white }}>
                                    <div className={styles.updatedRiskFieldLabel}>{label}</div>
                                    <div className={styles.updatedRiskOptions}>
                                        <RadioRow options={radioOptions} onChange={radioOptionId => updateRiskFieldBehaviour(radioOptionId as RiskToleranceUpdateBehaviour, riskField)} />
                                    </div>
                                </div>
                            );
                        })}
                    </Scrollable>
                </div>
            </div>
        </ ConfirmationModal>
    );
};
